import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Container,
  Paper,
  Chip,
  Avatar,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Groups3 as GroupIcon,
  ArrowOutward as ArrowOutwardIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Dangerous as EmergencyIcon,
  HealthAndSafety as HealthIcon,
  Support as SupportIcon,
  Psychology as PsychologyIcon,
  Favorite as FavoriteIcon,
  School as SchoolIcon,
} from "@mui/icons-material";
import useFitText from "../../util/FitText";

// Data structure for immediate resources
const IMMEDIATE_RESOURCES = [
  {
    id: "emergency",
    header: "Emergency Services",
    icon: <EmergencyIcon sx={{ color: "#d32f2f" }} />,
    color: "#d32f2f",
    contacts: [
      {
        name: "Campus Police",
        image:
          "https://uwaterloo.ca/special-constable-service/sites/default/files/uploads/images/uw_special_constable_service_logo_1.png",
        contact_info: [
          { method: "Office", value: "Commissary Building (COM)" },
          { method: "Email", value: "uwscs@uwaterloo.ca" },
          { method: "Phone", value: "519-888-4911" },
          {
            method: "Website",
            alternate_name: "UW Extension: 22222",
            value:
              "https://uwaterloo.ca/special-constable-service/campus-safety/phones-helplines",
          },
        ],
      },
      {
        name: "Waterloo Regional Police Service",
        image:
          "https://upload.wikimedia.org/wikipedia/en/thumb/9/9e/Waterloo_Regional_Police_Logo.svg/800px-Waterloo_Regional_Police_Logo.svg.png",
        contact_info: [
          { method: "Phone", alternate_name: "911 - Emergency", value: "911" },
          { method: "Phone", value: "519-570-9777" },
          { method: "Website", value: "https://www.wrps.on.ca" },
        ],
      },
    ],
  },
  {
    id: "racism-response",
    header: "Racism Response Contacts",
    icon: <SupportIcon sx={{ color: "#1976d2" }} />,
    color: "#1976d2",
    contacts: [
      {
        name: "Yessenia Guerrero",
        title: "Associate Director, Response",
        affiliation: "Office of Equity, Diversity, Inclusion and Anti-racism",
        image: null,
        contact_info: [{ method: "Email", value: "yguerrero@uwaterloo.ca" }],
      },
    ],
  },
  {
    id: "equity-office",
    header: "General Equity Office Contacts",
    icon: <SchoolIcon sx={{ color: "#388e3c" }} />,
    color: "#388e3c",
    contacts: [
      {
        name: "Equity Office",
        affiliation: "Waterloo Region",
        image: "/images/uw-crest.png",
        contact_info: [
          { method: "Email", value: "equity@uwaterloo.ca" },
          {
            method: "Website",
            alternate_name: "Request Support",
            value:
              "https://uwaterloo.ca/equity-diversity-inclusion-anti-racism/equity-office/edi-r-intake-form",
          },
        ],
      },
    ],
  },
  {
    id: "sexual-violence",
    header: "Sexual and Gender Violence Contacts",
    icon: <SupportIcon sx={{ color: "#7b1fa2" }} />,
    color: "#7b1fa2",
    contacts: [
      {
        name: "Sexual Assault Support Centre",
        affiliation: "Waterloo Region",
        image: "/images/inclusivity-wellness/SASC-logo.png",
        contact_info: [
          { method: "Phone", value: "(519) 741 8633" },
          {
            method: "Website",
            value: "https://www.sascwr.org/24-hour-support-line.html",
          },
        ],
      },
      {
        name: "Sexual Violence Prevention & Response Office",
        image: "/images/uw-crest.png",
        contact_info: [
          { method: "Email", value: "svpro@uwaterloo.ca" },
          {
            method: "Website",
            value:
              "https://uwaterloo.ca/sexual-violence-prevention-response-office/",
          },
        ],
      },
    ],
  },
  {
    id: "mental-health",
    header: "Mental Health Contacts",
    icon: <PsychologyIcon sx={{ color: "#f57c00" }} />,
    color: "#f57c00",
    contacts: [
      {
        name: "Good2Talk Helpline",
        title: "Post Secondary Student Helpline",
        affiliation: "Ontario",
        image:
          "https://good2talk.ca/wp-content/uploads/2021/12/on-good2talk-logo.svg",
        contact_info: [
          { method: "Phone", value: "1-866-925-5454" },
          { method: "Website", value: "https://good2talk.ca/ontario/" },
        ],
      },
      {
        name: "Here 24/7",
        title: "Mental Health and Addictions Support Line",
        affiliation: "Waterloo - Wellington",
        image: "https://here4hope.ca/wp-content/uploads/2019/11/Here-247.png",
        contact_info: [
          { method: "Phone", value: "1-844-437-3247" },
          { method: "Website", value: "https://here247.ca/" },
        ],
      },
      {
        name: "Grand River Hospital",
        title: "Mental Health and Support Info Line",
        affiliation: "Kitchener - Waterloo",
        image: "https://www.grhosp.on.ca/assets/images/interface/logo.svg",
        contact_info: [
          { method: "Phone", value: "519-749-4310" },
          {
            method: "Website",
            value:
              "https://www.grhosp.on.ca/care/services-departments/mental-health-addiction",
          },
        ],
      },
      {
        name: "Saint Mary's Hospital",
        title: "General Contact Line",
        affiliation: "Kitchener - Waterloo",
        image:
          "https://www.smgh.ca/work-with-us/careers/get-to-know-waterloo-region/smgh_rgb.png",
        contact_info: [
          { method: "Phone", value: "519-744-3311" },
          { method: "Website", value: "https://www.smgh.ca/" },
        ],
      },
    ],
  },
  {
    id: "friendly-folks",
    header: "Friendly Campus Folks",
    icon: <FavoriteIcon sx={{ color: "#e91e63" }} />,
    color: "#e91e63",
    contacts: [
      {
        name: "Engineering OTeam",
        title: "Orientation Organizers",
        image: "/images/inclusivity-wellness/headshots/OTeam_2025.jpg",
        contact_info: [{ method: "Email", value: "engoteam@uwaterloo.ca" }],
      },
      {
        name: "Jazz Fitzgerald",
        title: "Manager, Student Equity and Community",
        affiliation: "Student Success Office",
        image: "/images/inclusivity-wellness/headshots/Jazz-Fitzgerald.jpg",
        contact_info: [
          {
            method: "Email",
            displayText: "jazz.fitzgerald@uwaterloo.ca",
            value: "jazz.fitzgerald@uwaterloo.ca",
          },
          { method: "Office", value: "South Campus Hall (SCH) 236" },
        ],
      },
      {
        name: "Robin Jardin",
        title: "Student Relations Officer, Engineering Orientation Coordinator",
        affiliation: "Dean's office, Engineering Teaching & Student Experience",
        image: "/images/inclusivity-wellness/headshots/Robin-Jardin.jpg",
        contact_info: [
          { method: "Email", value: "rjardin@uwaterloo.ca" },
          { method: "Office", value: "Carl Pollock Hall (CPH) 2376C" },
        ],
      },
    ],
  },
];

// Data structure for follow-up resources
const FOLLOW_UP_RESOURCES = [
  {
    id: "equity-intake",
    name: "Equity Office EDI Intake Form",
    image: "/images/uw-crest.png",
    category: "Equity & Inclusion",
    blurb:
      "If you are looking for non-immediate support from the Office of Equity, Diversity, Inclusion and Anti-racism, fill out their form and they can get in contact with you however you want. This is the best way to start a conversation.",
    button_text: "📋 Fill out the form",
    button_link:
      "https://uwaterloo.ca/equity-diversity-inclusion-anti-racism/equity-office/edi-r-intake-form",
  },
  {
    id: "counselling",
    name: "UWaterloo Counselling Services",
    image: "/images/uw-crest.png",
    category: "Mental Health",
    blurb:
      "UWaterloo Counselling Services offers a variety of services to support students' mental health and wellness. They offer individual counselling, same-day counselling, workshops, and wellness programs.",
    button_text: "🖥️ Check out the website",
    button_link: "https://uwaterloo.ca/campus-wellness/counselling-services",
  },
  {
    id: "health-services",
    name: "UWaterloo Health Services",
    image: "/images/uw-crest.png",
    category: "Physical Health",
    blurb:
      "UWaterloo Health Services offers a variety of things to support students' physical health and wellness. They have a student medical clinic, immunizations, verification of illness services, information on student medical insurance, and more!",
    button_text: "🖥️ Check out the website",
    button_link: "https://uwaterloo.ca/campus-wellness/health-services",
  },
  {
    id: "indigenous-centre",
    name: "Indigenous Student Centre",
    image: "/images/uw-crest.png",
    category: "Community & Culture",
    blurb:
      "AANII, SHE:KON, BOOZHOO, HELLO, BONJOUR, TANSI! The Waterloo Indigenous Student Centre (WISC) is a friendly space on campus where you can connect with other Indigenous (First Nation, Inuit, and Metis) students and members of the Kitchener-Waterloo community. WISC is located in United College",
    button_text: "📃 Check out the Information Page",
    button_link:
      "https://uwaterloo.ca/united-college/waterloo-indigenous-student-centre",
  },
  {
    id: "international-support",
    name: "International Student Supports",
    image: "/images/uw-crest.png",
    category: "International Students",
    blurb:
      "The Student Success Office provides tons of information for International Students. Immigration consulting, tax information, health insurance, international peer communities, and more!",
    button_text: "✈️ Check out the resources",
    button_link:
      "https://uwaterloo.ca/student-success/international-student-resources",
  },
  {
    id: "accessibility",
    name: "Accessibility Services",
    image: "/images/uw-crest.png",
    category: "Accessibility",
    blurb:
      "Accessibility Services provides support in form of academic accommodations, learning strategy development, and more!",
    button_text: "📧 access@uwaterloo.ca",
    button_link: "mailto:access@uwaterloo.ca",
  },
];

// Contact method component with improved styling
const ContactMethod = ({ contact }) => {
  const { fontSize, ref: fontSizeRef } = useFitText({
    maxSize: 12,
    minSize: 10,
  });

  const displayText =
    contact.method === "Email"
      ? contact.displayText || contact.value
      : contact.value;

  const getButtonProps = () => {
    switch (contact.method) {
      case "Website":
        return {
          endIcon: <ArrowOutwardIcon />,
          onClick: () => window.open(contact.value, "_blank"),
          color: "primary",
        };
      case "Email":
        return {
          endIcon: <EmailIcon />,
          onClick: () => window.open(`mailto:${contact.value}`, "_blank"),
          color: "secondary",
        };
      case "Phone":
        return {
          endIcon: <PhoneIcon />,
          onClick: () => window.open(`tel:${contact.value}`, "_blank"),
          color: "secondary",
        };
      case "Office":
        return {
          disabled: true,
          style: { background: "none" },
        };
      default:
        return {
          variant: "outlined",
          disabled: true,
        };
    }
  };

  const buttonText = contact.alternate_name || displayText;

  return (
    <Button
      variant="contained"
      size="small"
      fullWidth
      sx={{
        minHeight: 36,
        textTransform: "none",
        borderRadius: 2,
        boxShadow: 1,
        "&:hover": {
          boxShadow: 3,
        },
        // Ensure text doesn't overflow
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
      {...getButtonProps()}
    >
      <Typography
        variant="body2"
        ref={fontSizeRef}
        style={{ fontSize }}
        sx={{
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "100%",
        }}
        title={contact.alternate_name || displayText} // Show full text on hover
      >
        {buttonText}
      </Typography>
    </Button>
  );
};

// Contact card component
const ContactCard = ({ contact }) => {
  return (
    <Card
      elevation={2}
      sx={{
        height: "100%",
        borderRadius: 3,
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 8,
        },
      }}
    >
      <Box p={2}>
        <Grid container spacing={2} alignItems="flex-start">
          {/* Image/Icon Section */}
          <Grid item xs={12} sm={4}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ height: 120 }}
            >
              {contact.image ? (
                <Avatar
                  src={contact.image}
                  alt={`${contact.name} ${contact.title || ""}`}
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: 2,
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 100,
                    height: 100,
                    fontSize: "2rem",
                    borderRadius: 2,
                  }}
                >
                  <PersonIcon />
                </Avatar>
              )}
            </Box>
          </Grid>

          {/* Content Section */}
          <Grid item xs={12} sm={8}>
            <Stack spacing={1} sx={{ width: "100%" }}>
              <Typography variant="h6" fontWeight="bold" color="text.primary">
                {contact.name}
              </Typography>

              {contact.title && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight="500"
                >
                  {contact.title}
                </Typography>
              )}

              {contact.affiliation && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontSize="0.875rem"
                >
                  {contact.affiliation}
                </Typography>
              )}

              {/* Contact Methods */}
              <Stack spacing={1} mt={1} sx={{ width: "100%" }}>
                {contact.contact_info.map((info, index) => (
                  <ContactMethod key={index} contact={info} />
                ))}
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

// Resource card component
const ResourceCard = ({ resource }) => {
  const { fontSize, ref: fontSizeRef } = useFitText({
    maxSize: 12,
    minSize: 10,
  });

  return (
    <Card
      elevation={2}
      sx={{
        height: "100%",
        borderRadius: 3,
        border: "2px solid #5D0096",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 8,
        },
      }}
    >
      <Box p={2}>
        <Grid container spacing={2}>
          {/* Image Section */}
          <Grid item xs={12} sm={4}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ height: 120 }}
            >
              <Avatar
                src={resource.image}
                alt={resource.name}
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: 2,
                  border: "2px solid #5D0096",
                }}
              />
            </Box>
          </Grid>

          {/* Content Section */}
          <Grid item xs={12} sm={8}>
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="text.primary"
                  gutterBottom
                >
                  {resource.name}
                </Typography>

                {resource.category && (
                  <Chip
                    label={resource.category}
                    size="small"
                    sx={{
                      bgcolor: "#5D0096",
                      color: "white",
                      fontSize: "0.75rem",
                    }}
                  />
                )}
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                ref={fontSizeRef}
                style={{ fontSize }}
                sx={{ lineHeight: 1.5 }}
              >
                {resource.blurb}
              </Typography>

              <Button
                variant="contained"
                color="primary"
                endIcon={<ArrowOutwardIcon />}
                onClick={() => window.open(resource.button_link, "_blank")}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {resource.button_text}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

// Header component with improved styling
const Header = () => {
  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: "#5D0096",
        color: "white",
        borderRadius: 0,
        py: 6,
        mb: 4,
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={4} alignItems="center" textAlign="center">
          <Typography
            variant="h2"
            fontWeight="bold"
            sx={{
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
            }}
          >
            Equity, Diversity, and Inclusivity
          </Typography>

          <Typography
            variant="h5"
            sx={{
              maxWidth: 800,
              lineHeight: 1.6,
              opacity: 0.95,
            }}
          >
            Engineering Orientation along with the University of Waterloo is
            committed to creating a welcoming environment and supporting you in
            your transition to university life.
          </Typography>

          <Typography
            variant="body1"
            sx={{
              maxWidth: 700,
              lineHeight: 1.7,
              opacity: 0.9,
            }}
          >
            If you need support, have concerns/feedback or are having trouble
            feeling welcome there are various resources including immediate
            resources, counselling, wellness activities and many others - we are
            eager to listen and ready to help.
          </Typography>

          <Button
            variant="contained"
            size="large"
            endIcon={<GroupIcon />}
            onClick={() => (window.location.href = "/resources/student-groups")}
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              color: "white",
              border: "2px solid rgba(255,255,255,0.3)",
              borderRadius: 3,
              px: 4,
              py: 1.5,
              fontSize: "1.1rem",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.3)",
                borderColor: "rgba(255,255,255,0.5)",
              },
            }}
          >
            See Affiliate Groups
          </Button>
        </Stack>
      </Container>
    </Paper>
  );
};

// Health and Wellness section
const HealthWellnessSection = () => {
  return (
    <Container maxWidth="lg" sx={{ mb: 6 }}>
      <Paper
        elevation={2}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "linear-gradient(135deg, #C2A8F0 0%, #5D0096 80%)",
          color: "white",
        }}
      >
        <Stack spacing={3} alignItems="center" textAlign="center">
          <HealthIcon sx={{ fontSize: 60, opacity: 0.9 }} />

          <Typography variant="h3" fontWeight="bold">
            Health and Wellness
          </Typography>

          <Typography
            variant="h6"
            sx={{
              maxWidth: 600,
              lineHeight: 1.6,
              opacity: 0.95,
            }}
          >
            University is a wild, exciting, and potentially stressful
            experience. Make sure you take care of yourself with some of these
            excellent resources.
          </Typography>

          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Professionals around campus, in person and remote counselling,
            knowledge bases, and much more are available to you.
          </Typography>
        </Stack>
      </Paper>
    </Container>
  );
};

// Immediate Resources section
const ImmediateResources = () => {
  const [expanded, setExpanded] = useState("emergency");

  const handleChange = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Container maxWidth="lg" sx={{ mb: 6 }}>
      <Typography variant="h3" textAlign="center" gutterBottom sx={{ mb: 4 }}>
        EDI Contacts
      </Typography>

      <Stack spacing={2}>
        {IMMEDIATE_RESOURCES.map((category) => (
          <Accordion
            key={category.id}
            expanded={expanded === category.id}
            onChange={handleChange(category.id)}
            sx={{
              borderRadius: 3,
              border: "none",
              outline: "none",
              boxShadow: "none",
              "&:before": { display: "none" },
              "&:hover, &:focus, &:active": {
                outline: "none",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: `${category.color}15`,
                borderRadius: 3,
                "&:hover": {
                  bgcolor: `${category.color}25`,
                },
              }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                {category.icon}
                <Typography
                  variant="h5"
                  fontWeight="600"
                  color={category.color}
                >
                  {category.header}
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 3 }}>
              <Grid container spacing={3}>
                {category.contacts.map((contact, index) => (
                  <Grid item xs={12} md={6} lg={4} key={index}>
                    <ContactCard contact={contact} />
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </Container>
  );
};

// Follow-up Resources section
const FollowUpResources = () => {
  return (
    <Container maxWidth="lg" sx={{ mb: 6 }}>
      <Typography variant="h3" textAlign="center" gutterBottom sx={{ mb: 4 }}>
        Additional Resources
      </Typography>

      <Grid container spacing={3}>
        {FOLLOW_UP_RESOURCES.map((resource, index) => (
          <Grid item xs={12} md={6} lg={4} key={index}>
            <ResourceCard resource={resource} />
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

// Main component
const InclusivityWellness = () => {
  return (
    <Box>
      <Header />
      <HealthWellnessSection />
      <ImmediateResources />
      <FollowUpResources />
    </Box>
  );
};

export default InclusivityWellness;
